import React, { useEffect, useState } from "react";
import Modal from "./components/Modal";
import articleService from "../../../service/article.service";
import reviewers from "../../../service/reviewers.service";
import Select from "react-select";
import "react-quill/dist/quill.snow.css";
import { useArticleManagement } from "../../../hooks/useArticleManagement";
import { useArticleNavigation } from "../../../hooks/useArticleNavigation";
import ArticleSearch from "../../../components/articles/ArticleSearch";
import ArticleTable from "../../../components/articles/ArticleTable";
import Pagination from "../../../components/articles/Pagination";
import useUserRole from "../../../utils/useUserRole";
import Swal from "sweetalert2";

const ArticleNouveaux = () => {
  const [reviewersList, setReviewersList] = useState([]);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [isLoadingReviewers, setIsLoadingReviewers] = useState(false);
  const userRole = useUserRole();

  // Hook de navigation réutilisable
  const { navigateToDetails } = useArticleNavigation();

  // Utilisation du hook partagé avec navigation personnalisée
  const {
    isLoading,
    error,
    searchTerm,
    currentItems,
    handleSearch,
    getArticleTitle,
    getArticleAuthor,
    getArticleDate,
    fetchArticles,
    currentPage,
    handlePageChange,
    itemsPerPage,
    handleItemsPerPageChange,
    indexOfFirstItem,
    indexOfLastItem,
    totalItems,
    totalPages,
    isModalOpen,
    setIsModalOpen,
    modalTitle,
    setModalTitle,
    modalSize,
    setModalSize,
    selectedArticle,
    setSelectedArticle,
    isSubmitting,
    setIsSubmitting,
    submitError,
    setSubmitError,
    submitSuccess,
    setSubmitSuccess,
    modalContent,
    setModalContent,
  } = useArticleManagement(() => articleService.getAll("nouveau", 100), null, {
    enableModal: true,
    enableEdit: false,
    customNavigationHandler: navigateToDetails, // Utiliser le hook de navigation
  });

  useEffect(() => {
    fetchReviewers();
  }, []);

  const fetchReviewers = async () => {
    setIsLoadingReviewers(true);
    try {
      const response = await reviewers.getAllSansPaginnation();
      if (response && response.data) {
        const formattedReviewers = response.data.data.map((reviewer) => ({
          value: reviewer.id_rev, // pour react-select
          label: `${reviewer.grade_rev || ""} ${reviewer.first_name || ""} ${
            reviewer.last_name || ""
          }`.trim(), // pour react-select
          id: reviewer.id_rev, // garder pour compatibilité
          name: `${reviewer.grade_rev || ""} ${reviewer.first_name || ""} ${
            reviewer.last_name || ""
          }`.trim(), // garder pour compatibilité
        }));
        setReviewersList(
          formattedReviewers.length > 0 ? formattedReviewers : []
        );
      } else {
        setReviewersList([]);
      }
    } catch (err) {
      setSubmitError("Failed to fetch reviewers.");
      setReviewersList([]);
    } finally {
      setIsLoadingReviewers(false);
    }
  };

  // Fonction pour formatter l'affichage des auteurs multiples
  const getFormattedAuthors = (article) => {
    let authors = getArticleAuthor(article);

    // Si l'article a des co-auteurs, les ajouter à la liste
    if (article.coauthors && article.coauthors.length > 0) {
      const coauthorNames = article.coauthors.map((coauthor) =>
        `${coauthor.first_name || ""} ${coauthor.last_name || ""}`.trim()
      );
      authors = `${authors}, ${coauthorNames.join(", ")}`;
    }

    return authors;
  };

  // Fonctions adaptées pour react-select
  const handleSelectChange = (selectedOptions) => {
    setSelectedReviewers(selectedOptions || []);
    console.log(
      "Nombre de relecteurs sélectionnés:",
      selectedOptions ? selectedOptions.length : 0
    );
  };

  const reloadTableSection = async () => {
    try {
      await fetchArticles();
    } catch (error) {
      console.error("Erreur lors du rechargement du tableau:", error);
    }
  };

  const handleSubmitReviewers = async () => {
    if (selectedReviewers.length < 2) {
      Swal.fire({
        icon: "warning",
        title: "Sélection insuffisante",
        text: "Veuillez sélectionner au moins deux relecteurs.",
      });
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const submissionData = {
        id_art: selectedArticle.id_art,
        id_rev: selectedReviewers.map((option) => option.value),
      };
      await reviewers.evaluationManagers(submissionData);
      setSubmitSuccess(true);

      Swal.fire({
        icon: "success",
        title: "Relecteurs affectés",
        text: "Les relecteurs ont été affectés avec succès!",
        timer: 1500,
        showConfirmButton: false,
      });

      await reloadTableSection();
      setIsModalOpen(false);
    } catch (err) {
      setSubmitError(
        err.message ||
          "Une erreur s'est produite lors de l'affectation des relecteurs."
      );

      // Afficher une erreur
      Swal.fire({
        icon: "error",
        title: "Échec de l'affectation",
        text:
          err.message ||
          "Une erreur s'est produite lors de l'affectation des relecteurs.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Empêcher l'action si admin
  const handleAffecterRelecteur = (article) => {
    if (userRole === "Administrateur") {
      setModalTitle("Action non autorisée");
      setModalSize("md");
      setModalContent(
        <div className="text-center font-semibold text-red-600">
          Les administrateurs ne peuvent pas affecter des relecteurs.
        </div>
      );
      setIsModalOpen(true);
      return;
    }
    setSelectedArticle(article);
    setSelectedReviewers([]);
    setSubmitError(null);
    setSubmitSuccess(false);
    setModalTitle("Affecter des relecteurs");
    setModalSize("xl"); // Changé de 'lg' à 'xl' pour agrandir le popup
    updateModalContent(article);
    setIsModalOpen(true);
  };

  // Utilisation de useEffect pour surveiller les changements de selectedReviewers
  useEffect(() => {
    if (modalTitle === "Affecter des relecteurs" && selectedArticle) {
      updateModalContent(selectedArticle);
    }
  }, [modalTitle, selectedArticle, selectedReviewers]);

  const updateModalContent = (article) => {
    setModalContent(
      <div className="space-y-6">
        <p className="text-gray-600">
          Sélectionnez les relecteurs pour l'article "{getArticleTitle(article)}
          "
        </p>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="reviewers"
              className="block text-sm font-medium text-gray-700"
            >
              Relecteurs
            </label>
            {/* Notification indiquant le minimum de 2 relecteurs */}
            <div className="mb-2 mt-1 flex items-center rounded-md bg-amber-50 p-2 text-sm font-semibold text-amber-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2 h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Un minimum de 2 relecteurs est requis.
            </div>
            <div className="mt-1">
              {isLoadingReviewers ? (
                <div className="flex items-center justify-center p-4">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-[#57A863]"></div>
                  <span className="ml-2">Chargement des relecteurs...</span>
                </div>
              ) : (
                <>
                  <div className="mb-2 flex items-center justify-between text-xs">
                    <span className="text-gray-400">
                      Options disponibles: {reviewersList.length}
                    </span>
                    {/* Afficher le nombre de relecteurs sélectionnés avec indication visuelle */}
                    <span
                      className={`font-medium ${
                        selectedReviewers.length >= 2
                          ? "text-green-600"
                          : "text-amber-600"
                      }`}
                    >
                      Sélectionnés: {selectedReviewers.length}/2 minimum
                    </span>
                  </div>
                  {/* Remplacer par react-select avec menu plus grand */}
                  <Select
                    isMulti
                    name="reviewers"
                    options={reviewersList}
                    value={selectedReviewers}
                    onChange={handleSelectChange}
                    placeholder="Sélectionnez les relecteurs"
                    noOptionsMessage={() => "Aucun relecteur disponible"}
                    classNamePrefix="select"
                    className="basic-multi-select"
                    maxMenuHeight={400} // Augmenté de 300 à 400 pour afficher plus d'options
                    menuPlacement="auto"
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: "#d1d5db",
                        borderRadius: "0.375rem",
                        padding: "0.25rem", // Augmenté de 0.1rem à 0.25rem pour un meilleur confort visuel
                        minHeight: "50px", // Ajouté pour agrandir le contrôle
                      }),
                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#57A863",
                        color: "white",
                        padding: "2px", // Ajouté pour plus d'espace
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "white",
                        fontSize: "0.9rem", // Légèrement augmenté
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "white",
                        ":hover": {
                          backgroundColor: "#4a9154",
                          color: "white",
                        },
                        padding: "2px", // Ajouté pour plus d'espace
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected
                          ? "#57A863"
                          : state.isFocused
                          ? "#57A86315"
                          : base.backgroundColor,
                        padding: "10px 12px", // Augmenté pour plus d'espace entre les options
                        fontSize: "0.9rem", // Légèrement augmenté
                      }),
                    }}
                  />
                </>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Sélectionnez au moins deux relecteurs pour évaluer cet article
            </p>
          </div>
          {submitError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              <p>{submitError}</p>
            </div>
          )}
          {submitSuccess && (
            <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">
              <p>Les relecteurs ont été affectés avec succès!</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Bouton de rechargement manuel
  const handleRefresh = () => {
    reloadTableSection();
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-[#57A863]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-center text-red-800">
        <p>{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <ArticleSearch
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        title="Articles soumis" // Changé de "Nouveaux Articles" à "Articles soumis"
        subtitle="Liste des articles récemment soumis en attente d'affectation"
      />

      {/* Section de la table qui sera rechargée individuellement */}
      <div className="mb-6 overflow-hidden rounded-lg">
        <ArticleTable
          currentItems={currentItems}
          getArticleTitle={getArticleTitle}
          getArticleAuthor={getFormattedAuthors}
          getArticleDate={getArticleDate}
          handleActionPrimary={
            userRole === "Administrateur" ? undefined : handleAffecterRelecteur
          }
          handleViewDetails={navigateToDetails} // Utiliser le hook de navigation
          actionPrimaryText={userRole === "Administrateur" ? null : "Affecter"}
          showPrimaryButton={userRole !== "Administrateur"}
          showEditButton={false}
          showEvaluatorsColumn={false}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          handleItemsPerPageChange={handleItemsPerPageChange}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          totalItems={totalItems}
        />
      </div>

      {/* Modal - Pas besoin de classes spéciales d'overflow avec react-select */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        width={modalSize}
      >
        <div className="p-6">{modalContent}</div>

        {/* Footer avec boutons */}
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <button
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            onClick={() => setIsModalOpen(false)}
            disabled={isSubmitting}
          >
            Annuler
          </button>
          {modalTitle === "Affecter des relecteurs" && (
            <button
              className={`rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm ${
                isSubmitting ||
                userRole === "Administrateur" ||
                selectedReviewers.length < 2
                  ? "cursor-not-allowed bg-indigo-400"
                  : "bg-[#57A863] hover:bg-[#4a9154]"
              }`}
              onClick={handleSubmitReviewers}
              disabled={
                isSubmitting ||
                isLoadingReviewers ||
                userRole === "Administrateur" ||
                selectedReviewers.length < 2
              }
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Affectation en cours...
                </span>
              ) : (
                "Affecter les relecteurs"
              )}
            </button>
          )}
          {modalTitle === "Détails de l'article" && (
            <button
              className="rounded-md bg-[#57A863] px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#4a9154]"
              onClick={() => setIsModalOpen(false)}
            >
              Fermer
            </button>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ArticleNouveaux;
